import { useCallback, useMemo } from 'react';

import { type EAccessCheckTrackingStatus } from '@/packages/EarlyAccessPaywall/shared/components/Provider/Provider';
import { useUserQuery } from '@/packages/query-state/shared/store/monolith-python/cian-api/site/v1/get-user-data-by-request/query';

import { useCAForCard } from '../../../hooks/card/useCAForCard';

const CATEGORY = 'Phones';

type TTracking = {
  trackEarlyAccessConfirmed: VoidFunction;
  trackEarlyAccessBlocked: (trackingStatus: EAccessCheckTrackingStatus) => void;
  trackPhoneButtonClick: VoidFunction;
};

export const usePhoneTracking = (): TTracking => {
  const ca = useCAForCard();

  const { data: user } = useUserQuery();

  const trackEarlyAccessConfirmed = useCallback(() => {
    ca({ category: CATEGORY, action: 'Open_listing_in_map_early_access' }, { label: true });
  }, [ca]);

  const trackEarlyAccessBlocked = useCallback(
    (trackingStatus: EAccessCheckTrackingStatus) => {
      ca(
        { category: CATEGORY, action: 'Blocked_listing_in_map_early_access' },
        { label: true, pageExtra: { block_type: trackingStatus } },
      );
    },
    [ca],
  );

  const trackPhoneButtonClick = useCallback(() => {
    ca({ category: CATEGORY, action: user?.isAgent ? 'Open_listing_agent_block' : 'Open_listing' }, { label: true });
  }, [ca, user]);

  return useMemo(
    () => ({
      trackEarlyAccessConfirmed,
      trackEarlyAccessBlocked,
      trackPhoneButtonClick,
    }),
    [trackEarlyAccessConfirmed, trackEarlyAccessBlocked, trackPhoneButtonClick],
  );
};
